import type {
  ExternalOrgParams,
  InviteTokenData,
  SupportRequestTokenData,
} from '@/lib/api/external-orgs.api'
import { externalOrgApi } from '@/lib/api/external-orgs.api'
import { createLogger } from '@/lib/logger'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery } from '@tanstack/react-query'

import type {
  ExternalOrgInvite,
  ExternalOrgMember,
  ExternalOrgRelationship,
} from '@scrambl/shared'

import { queryKeys } from '../query-keys'

const logger = createLogger('hooks/queries/useExternalOrgsQuery')

const externalOrgsLogState = {
  lastLogTsByKey: new Map<string, number>(),
}

const debugLogExternalOrgs = (
  key: string,
  message: string,
  payload?: unknown,
) => {
  if (!import.meta.env.DEV) return

  const now = Date.now()
  const lastLog = externalOrgsLogState.lastLogTsByKey.get(key) ?? 0
  if (now - lastLog < 300) {
    return
  }

  externalOrgsLogState.lastLogTsByKey.set(key, now)
  logger.debug(message, payload)
}

// =====================
// Invite Queries
// =====================

/**
 * Hook to fetch sent invites (as host org)
 */
export function useSentInvitesQuery(
  params?: ExternalOrgParams,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ExternalOrgInvite[]>({
    queryKey: queryKeys.externalOrgs.sentInvites(params),
    queryFn: () => externalOrgApi.getSentInvites(params),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook to fetch received invites (as partner org)
 */
export function useReceivedInvitesQuery(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ExternalOrgInvite[]>({
    queryKey: queryKeys.externalOrgs.receivedInvites(),
    queryFn: () => externalOrgApi.getReceivedInvites(),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook to fetch invite details by token (public)
 * This is used on the invite acceptance page
 */
export function useInviteByTokenQuery(
  token: string | null | undefined,
  options?: {
    enabled?: boolean
  },
) {
  return useQuery<InviteTokenData>({
    queryKey: queryKeys.externalOrgs.inviteByToken(token || ''),
    queryFn: () => externalOrgApi.getInviteByToken(token!),
    enabled: options?.enabled !== false && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes - invite data doesn't change often
    gcTime: 10 * 60 * 1000,
    retry: 1,
  })
}

// =====================
// Partner Org Queries (Host Perspective)
// =====================

/**
 * Hook to fetch partner orgs (external orgs that access our workspace)
 */
export function usePartnerOrgsQuery(
  params?: ExternalOrgParams,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ExternalOrgRelationship[]>({
    queryKey: queryKeys.externalOrgs.partners(params),
    queryFn: () => externalOrgApi.getPartnerOrgs(params),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook to fetch a single partner relationship by ID
 */
export function usePartnerQuery(
  relationshipId: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ExternalOrgRelationship>({
    queryKey: queryKeys.externalOrgs.partner(relationshipId || ''),
    queryFn: () => externalOrgApi.getPartnerById(relationshipId!),
    enabled:
      options?.enabled !== false &&
      !!relationshipId &&
      !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Hook to fetch external members for a partner relationship
 */
export function usePartnerMembersQuery(
  relationshipId: string | undefined,
  params?: ExternalOrgParams,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ExternalOrgMember[]>({
    queryKey: queryKeys.externalOrgs.partnerMembers(
      relationshipId || '',
      params,
    ),
    queryFn: () => externalOrgApi.getPartnerMembers(relationshipId!, params),
    enabled:
      options?.enabled !== false &&
      !!relationshipId &&
      !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

// =====================
// Host Org Queries (Partner Perspective)
// =====================

/**
 * Hook to fetch host orgs (workspaces we can access as partner)
 */
export function useHostOrgsQuery(
  params?: ExternalOrgParams,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ExternalOrgRelationship[]>({
    queryKey: queryKeys.externalOrgs.hostOrgs(params),
    queryFn: () => externalOrgApi.getHostOrgs(params),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook to fetch members with access to a host org
 */
export function useHostOrgMembersQuery(
  relationshipId: string | undefined,
  params?: ExternalOrgParams,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ExternalOrgMember[]>({
    queryKey: queryKeys.externalOrgs.hostOrgMembers(
      relationshipId || '',
      params,
    ),
    queryFn: () => externalOrgApi.getHostOrgMembers(relationshipId!, params),
    enabled:
      options?.enabled !== false &&
      !!relationshipId &&
      !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook to fetch all host orgs that the current user has access to
 * through external org memberships. This does NOT require an organization
 * context - it returns all accessible host orgs across all of the user's
 * organizations.
 */
export function useMyHostOrgsQuery(options?: { enabled?: boolean }) {
  const { isAuthenticated } = useAuth()

  return useQuery<ExternalOrgRelationship[]>({
    queryKey: queryKeys.externalOrgs.myHostOrgs(),
    queryFn: () => externalOrgApi.getMyHostOrgs(),
    enabled: options?.enabled !== false && isAuthenticated,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

// =====================
// Assignable External Members
// =====================

/**
 * Hook to fetch all external members that can be assigned to tasks/projects
 * in this organization. Returns members from partner orgs where showInAssignees
 * is enabled on the relationship.
 */
// =====================
// Support Request Queries (Partner-initiated)
// =====================

/**
 * Hook to fetch support requests sent by the current org (as partner)
 */
export function useSentSupportRequestsQuery(
  params?: ExternalOrgParams,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ExternalOrgInvite[]>({
    queryKey: queryKeys.externalOrgs.sentSupportRequests(params),
    queryFn: () => externalOrgApi.getSentSupportRequests(params),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook to fetch pending support requests addressed to the current user (as host admin)
 */
export function useIncomingSupportRequestsQuery(options?: {
  enabled?: boolean
}) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ExternalOrgInvite[]>({
    queryKey: queryKeys.externalOrgs.incomingSupportRequests(),
    queryFn: () => externalOrgApi.getIncomingSupportRequests(),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook to fetch support request details by token (public, for the accept page)
 */
export function useSupportRequestByTokenQuery(
  token: string | null | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery<SupportRequestTokenData>({
    queryKey: queryKeys.externalOrgs.supportRequestByToken(token || ''),
    queryFn: () => externalOrgApi.getSupportRequestByToken(token!),
    enabled: options?.enabled !== false && !!token,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  })
}

export function useAssignableExternalMembersQuery(options?: {
  enabled?: boolean
}) {
  const { currentOrganization } = useOrganizationStore()
  const isExternalUser = currentOrganization?.isExternalAccess === true

  return useQuery<ExternalOrgMember[]>({
    queryKey: queryKeys.externalOrgs.assignableMembers(currentOrganization?.id),
    queryFn: async () => {
      const startAt = Date.now()
      const result = await externalOrgApi.getAssignableExternalMembers()
      debugLogExternalOrgs(
        'assignable-members',
        'useAssignableExternalMembersQuery loaded',
        {
          elapsedMs: Date.now() - startAt,
          count: result.length,
        },
      )
      return result
    },
    enabled:
      options?.enabled !== false &&
      !!currentOrganization?.id &&
      !isExternalUser,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  })
}
